import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, BookOpen, Building2, Landmark, MapPin } from "lucide-react";

const TRACK_ACCENTS = {
  foundations: "bg-[#0A0A0A] text-white",
  india: "bg-[#C5A059] text-[#0A0A0A]",
  dubai: "bg-white text-[#0A0A0A] border border-[#0A0A0A]",
};

export default function Landing() {
  const [tracks, setTracks] = useState([]);

  useEffect(() => {
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="border-b border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-6">
              <span className="text-[#C5A059]">01</span> · A curriculum for property
            </div>
            <h1 data-testid="hero-title" className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#0A0A0A] font-black mb-8">
              Learn real estate<br/>
              like the people who <em className="italic font-normal">quietly</em> own it.
            </h1>
            <p className="text-lg lg:text-xl text-[#525252] leading-relaxed max-w-2xl mb-10">
              Two markets. Two tracks. Written natively — India in rupees, RERA and CIBIL; Dubai in dirhams, DLD and EIBOR.
              No mashing them together. Nine modules, thirty chapters, zero fluff.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <Link
                data-testid="hero-cta-modules"
                to="/modules"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#0A0A0A] text-[#FFFFFF] hover:bg-[#000000] transition-colors font-medium btn-primary-hover"
              >
                Choose your track <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                data-testid="hero-cta-signup"
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFFFFF] transition-colors font-medium"
              >
                Create free account
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              <Link data-testid="hero-track-india" to="/modules#india" className="relative group overflow-hidden">
                <img src="https://images.pexels.com/photos/37352236/pexels-photo-37352236.jpeg" alt="India" className="w-full h-[420px] object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/95 via-[#0A0A0A]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-1">Track</div>
                  <div className="font-serif text-white text-2xl font-bold">India</div>
                  <div className="text-white/80 text-xs mt-1 font-serif italic">Appreciation. RERA. Section 54.</div>
                </div>
              </Link>
              <Link data-testid="hero-track-dubai" to="/modules#dubai" className="relative group overflow-hidden">
                <img src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c" alt="Dubai" className="w-full h-[420px] object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/95 via-[#0A0A0A]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-1">Track</div>
                  <div className="font-serif text-white text-2xl font-bold">Dubai</div>
                  <div className="text-white/80 text-xs mt-1 font-serif italic">Yield. DLD. Golden Visa.</div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-b border-[#E5E5E0] bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-0 border-l border-r border-[#E5E5E0]">
          {[
            { icon: BookOpen, label: "Written for readers", body: "Long-form chapters, not bite-sized slop. Read like a good textbook, cover to cover." },
            { icon: Building2, label: "Native to each market", body: "India in rupees and RERA. Dubai in dirhams and DLD. Vocabulary that matches the market." },
            { icon: Landmark, label: "Practical, not academic", body: "Frameworks used by working investors and top-performing agents. Concrete numbers throughout." },
          ].map((p, i) => (
            <div key={i} className="p-8 border-r border-[#E5E5E0] last:border-r-0 border-b md:border-b-0">
              <p.icon className="w-6 h-6 text-[#C5A059] mb-4" strokeWidth={1.5} />
              <div className="font-serif text-xl font-semibold text-[#0A0A0A] mb-2">{p.label}</div>
              <p className="text-[#525252] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tracks */}
      {tracks.map((track) => (
        <section id={track.id} key={track.id} className="max-w-7xl mx-auto px-6 py-20 border-b border-[#E5E5E0] last:border-b-0">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${TRACK_ACCENTS[track.id]}`}>
                  {track.label}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#525252]">{track.modules.length} modules</span>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#0A0A0A] font-bold tracking-tight max-w-2xl">
                {track.tagline}
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#E5E5E0]" data-testid={`landing-track-${track.id}`}>
            {track.modules.map((m) => (
              <Link
                key={m.id}
                data-testid={`landing-module-${m.id}`}
                to={`/modules/${m.id}`}
                className="group p-8 border-r border-b border-[#E5E5E0] hover:bg-[#F5F5F0] transition-colors block"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="font-mono text-3xl font-medium text-[#C5A059]">{m.number}</div>
                  <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#525252]">
                    <MapPin className="w-3 h-3" /> {m.region}
                  </div>
                </div>
                <h3 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-3 leading-tight group-hover:text-[#000000]">{m.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed mb-6 line-clamp-3">{m.summary}</p>
                <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider text-[#525252]">
                  <span>{m.chapter_count} chapters</span>
                  <span>{m.reading_time} min read</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
